import { Subscriber } from '@gala-node/pubsub-client'

const POT_SIZE_CHANGED_CHANNEL_NAME = 'potSizeChanged'

export interface INotifications {
  potSizeChanged: (newAmount: number) => Promise<void>
}

export interface INotificationReceiver {
  on<TNotificationName extends keyof INotifications>(
    event: TNotificationName,
    listener: (
      ...args: Parameters<INotifications[TNotificationName]>
    ) => Promise<void>
  ): void
}

const galaNodePubSubApiUri = process.env.REACT_APP_GALA_NODE_PUBSUB_API_URI ?? ''
const galaNodePubSubTenantId =
  process.env.REACT_APP_GALA_NODE_PUBSUB_TENANT_ID ?? ''
const galaNodePubSubPublicKey =
  process.env.REACT_APP_GALA_NODE_PUBSUB_PUBLIC_KEY ?? ''

export class GalaNodeNotificationReceiver implements INotificationReceiver {
  private subscriber: Subscriber
  private listeners: Partial<
    Record<keyof INotifications, Array<(...args: any[]) => Promise<void>>>
  > = {}

  constructor() {
    this.subscriber = new Subscriber({
      apiUrl: galaNodePubSubApiUri,
      tenantId: galaNodePubSubTenantId,
      tenantPublicKey: galaNodePubSubPublicKey,
      subject: POT_SIZE_CHANGED_CHANNEL_NAME,
    })

    this.subscriber.connect()

    this.subscriber.onMessage((data: string) => {
      const obj = JSON.parse(data)
      const potSizeChangedPayload = asPotSizeChangedPayload(obj)
      if (potSizeChangedPayload) {
        this.emit(
          POT_SIZE_CHANGED_CHANNEL_NAME,
          potSizeChangedPayload.newAmount
        )
      }
    })
  }

  onPotSizeChanged<TNotificationName extends keyof INotifications>(
    listener: (
      ...args: Parameters<INotifications[TNotificationName]>
    ) => Promise<void>
  ): void {
    const notification = POT_SIZE_CHANGED_CHANNEL_NAME as TNotificationName
    this.on(notification, listener)
  }

  on<TNotificationName extends keyof INotifications>(
    notification: TNotificationName,
    listener: (
      ...args: Parameters<INotifications[TNotificationName]>
    ) => Promise<void>
  ): void {
    const existingListeners: Array<INotifications[TNotificationName]> =
      this.listeners[notification] ?? []
    existingListeners.push(listener as any)
    this.listeners[notification] = existingListeners
  }

  private async emit<TNotificationName extends keyof INotifications>(
    event: TNotificationName,
    ...args: Parameters<INotifications[TNotificationName]>
  ): Promise<void> {
    const listeners = this.listeners[event]
    if (!listeners) {
      return
    }
    const results = await Promise.allSettled(
      listeners.map((listener) => listener(...args))
    )

    for (const result of results) {
      if (result.status === 'rejected') {
        console.error(result.reason)
      }
    }
  }
}

function asPotSizeChangedPayload(payload: unknown) {
  if (!(payload && typeof payload === 'object')) {
    return undefined
  }

  if (!('newAmount' in payload)) {
    return undefined
  }

  const newAmount = payload.newAmount

  if (typeof newAmount !== 'number') {
    return undefined
  }

  return { newAmount }
}
